import { createFileRoute } from "@tanstack/react-router";
import { UserDetailsPage } from "@/components/users/[id]";
import { adminExtend, sites } from "@/lib/auth-client";

export type LoaderResult = {
  user: {
    id: string;
    email: string;
    name?: string | null;
    image?: string | null;
    emailVerified?: boolean | null;
    role?: string | null;
    banned?: boolean | null;
    banReason?: string | null;
    createdAt?: string | Date;
    updatedAt?: string | Date;
  } | null;
  organizations: Array<{
    organizationId: string;
    organizationName: string;
    sites: Array<{
      id: string;
      name: string;
      isMember: boolean;
    }>;
  }>;
};

export const Route = createFileRoute("/dashboard/_admin/user/$userId")({
  loader: async ({ params }): Promise<LoaderResult> => {
    // Source both the target user and the available sites from manager-capable
    // endpoints. For admins these return everything; for managers they are
    // scoped server-side to the sites the manager belongs to. Avoids the
    // admin-only admin.listUsers / organization.listByUser / list-organizations
    // calls that 403 for managers.
    let user: LoaderResult["user"] = null;
    const userSiteIds = new Set<string>();
    try {
      const { data, error } = await adminExtend.listUsersDetailed();
      if (error) throw error;
      const list = (data?.users ?? []) as Array<{
        id: string;
        email: string;
        name: string | null;
        role: string | null;
        emailVerified?: boolean | null;
        banned?: boolean | null;
        createdAt?: string | Date;
        updatedAt?: string | Date;
        sites?: Array<{ id: string }>;
      }>;
      const u = list.find((x) => x.id === params.userId) ?? null;
      if (u) {
        user = {
          id: u.id,
          email: u.email,
          name: u.name,
          role: u.role,
          emailVerified: u.emailVerified ?? null,
          banned: u.banned ?? null,
          createdAt: u.createdAt,
          updatedAt: u.updatedAt,
        };
        for (const s of u.sites ?? []) userSiteIds.add(s.id);
      }
    } catch (e) {
      console.error("Failed to fetch user:", e);
    }

    // Build the org → sites tree from the sites the *current* user can see.
    const organizations: LoaderResult["organizations"] = [];
    try {
      const { data, error } = await sites.listUserSites();
      if (error) throw error;
      const byOrg = new Map<string, LoaderResult["organizations"][number]>();
      for (const s of data?.sites ?? []) {
        const entry = byOrg.get(s.organizationId) ?? {
          organizationId: s.organizationId,
          organizationName: s.organization?.name ?? "Assets",
          sites: [],
        };
        entry.sites.push({
          id: s.id,
          name: s.name,
          isMember: userSiteIds.has(s.id),
        });
        byOrg.set(s.organizationId, entry);
      }
      organizations.push(...byOrg.values());
    } catch (e) {
      console.error("Failed to fetch sites:", e);
    }

    return { user, organizations };
  },
  component: Component,
  staticData: {
    title: "User Details",
    parent: { title: "User Management", path: "/dashboard/user-management" },
  },
});

function Component() {
  const data = Route.useLoaderData();
  return <UserDetailsPage data={data} />;
}
